/**
 * inlineImageSrc.ts — which image `src` values may be spliced into rich HTML.
 *
 * Preview and thumbnail sources are written into a quoted `src` attribute of
 * HTML that has already been sanitized, so a value must never be able to close
 * the attribute or the tag, and must never be an active document type. That
 * leaves raster image data URLs (base64 only; `image/svg+xml` can script) and
 * blob:/file:/http(s): URLs with no quote, angle bracket, backtick or space.
 */

/**
 * A raster image data URL, base64 only. The types cover INLINE_PHOTO_MIME_TYPES
 * (`src/constants/inlineImageMimeTypes.ts`) plus the `x-`/`p` variants Android
 * pickers report; all are inert inside `<img>`.
 */
export const RASTER_IMAGE_DATA_URL =
  /^data:image\/(?:png|x-png|jpe?g|pjpeg|gif|webp|bmp|x-ms-bmp|tiff?|heic|heif|avif);base64,[A-Za-z0-9+/]+={0,2}$/i;

const PREVIEW_URL = /^(?:blob|file|https?):[^\s"'<>`]+$/i;
const LOCAL_PREVIEW_URL = /^(?:blob|file):[^\s"'<>`]+$/i;

export function isRasterImageDataUrl(src: string): boolean {
  return RASTER_IMAGE_DATA_URL.test(src.trim());
}

/** A just-picked or offline image: a blob: object URL (web) or file: URI (native). */
export function isLocalPreviewImageSrc(src: string): boolean {
  return LOCAL_PREVIEW_URL.test(src.trim());
}

/** True when `src` is safe to splice into a quoted `src` attribute and display. */
export function isSafeInlineImageSrc(src: string): boolean {
  const trimmed = src.trim();
  return RASTER_IMAGE_DATA_URL.test(trimmed) || PREVIEW_URL.test(trimmed);
}
